
interface FilterSectionProps {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  priceRange: number[];
  setPriceRange: (range: number[]) => void;
  selectedFeatures: string[];
  setSelectedFeatures: (features: string[]) => void;
}

export default function FilterSection({
  selectedCategory,
  setSelectedCategory,
  priceRange,
  setPriceRange,
  selectedFeatures,
  setSelectedFeatures
}: FilterSectionProps) {
  const categories = [
    { id: 'all', name: 'All Vehicles', icon: 'ri-car-line' },
    { id: 'economy', name: 'Economy', icon: 'ri-car-line' },
    { id: 'sedan', name: 'Sedan', icon: 'ri-car-line' },
    { id: 'suv', name: 'SUV', icon: 'ri-truck-line' },
    { id: 'luxury', name: 'Luxury', icon: 'ri-car-line' },
    { id: 'van', name: 'Van & Truck', icon: 'ri-truck-line' }
  ];

  const features = [
    'Automatic Transmission',
    'Air Conditioning',
    'GPS Navigation',
    'Bluetooth',
    'Backup Camera',
    'Heated Seats',
    'Sunroof',
    'All-Wheel Drive'
  ];

  const handleFeatureToggle = (feature: string) => {
    if (selectedFeatures.includes(feature)) {
      setSelectedFeatures(selectedFeatures.filter(f => f !== feature));
    } else {
      setSelectedFeatures([...selectedFeatures, feature]);
    }
  };

  return (
    <section className="bg-gray-50 py-12">
      <div className="container mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Vehicle Fleet</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose from our diverse collection of well-maintained vehicles, perfect for any occasion
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Vehicle Categories</h3>
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                }`}
              >
                <i className={`${category.icon} mr-2`}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Price Range Filter */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Price Range (per day)</h3>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-600">${priceRange[0]}</span>
                <span className="text-gray-600">${priceRange[1]}</span>
              </div>
              <input
                type="range"
                min="0"
                max="200"
                value={priceRange[1]}
                onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>

          {/* Features Filter */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Features</h3>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="grid grid-cols-2 gap-3">
                {features.map((feature) => (
                  <label key={feature} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedFeatures.includes(feature)}
                      onChange={() => handleFeatureToggle(feature)}
                      className="mr-3 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
